/* MolCellQuest — 正解したときに起こることを1か所にまとめる
   進化ポイントの付与 → 時代・チェックポイントの判定 → 演出 → 保存。 */

import { progress, save } from "./state.svelte.js";
import {
  addEvo,
  solveEvo,
  recordAttempt,
  recordCorrect,
  breakCombo,
  checkNewBadges,
  claimDailyMission,
} from "./economy.js";
import { recordMiss, clearMiss } from "./missed.svelte.js";
import { eraForEvo, checkpointsBetween, erasEnteredBetween } from "../../data/journeyEngine.js";
import { ERA_BY_ID } from "../../data/eras.js";
import { startConfetti, showToast, floatEvo, comboFloat } from "./effects.js";
import { playSound } from "./audio.js";
import { fmtEvo } from "./format.js";

/** 旅ビューだけが扱う一時的な UI 状態（永続化しない） */
export const journeyUi = $state({
  pendingStory: null, // 昇格直後に見せるストーリーの時代 id
  morphFrom: null, // 姿が変わったときの「前の形態」
});

/**
 * evo が before → after に増えたときの節目判定と演出。
 * @returns {{ eraUp: object|null, checkpoints: array }}
 */
export function checkJourneyProgress(before, after) {
  if (after <= before) return { eraUp: null, checkpoints: [] };

  // チェックポイント（軽い演出）
  const cps = checkpointsBetween(before, after);
  const fresh = [];
  for (const cp of cps) {
    if (progress.milestonesReached.includes(cp.id)) continue;
    progress.milestonesReached.push(cp.id);
    fresh.push(cp);
  }

  // 時代の昇格（大きい演出）
  const entered = erasEnteredBetween(before, after);
  let eraUp = null;
  if (entered.length > 0) {
    // 一気に複数の時代を飛び越えたときは、いちばん先の時代だけ祝う
    const last = entered[entered.length - 1];
    eraUp = last.era;

    const prevEra = ERA_BY_ID[progress.eraId];
    if (prevEra && prevEra.form !== last.era.form) journeyUi.morphFrom = prevEra.form;

    progress.eraId = last.era.id;
    startConfetti(160);

    playSound("levelup");
    if (!progress.storiesSeen.includes(last.era.id)) {
      // 「見た」と記録するのは実際に表示して閉じたとき（Journey.svelte）。
      // ここで記録してしまうと、旅タブを開く前にリロードした人が
      // ストーリーを永久に見られなくなる。
      journeyUi.pendingStory = last.era.id;
    } else {
      showToast(
        `${last.era.em} 新しい時代に到達！<br><b>${last.era.name}</b>` +
          `<br><span class="tiny">${last.era.desc}</span>`,
        5
      );
    }
  } else if (fresh.length > 0) {
    startConfetti(80);
    playSound("checkpoint");
  }

  // チェックポイントのトーストは時代昇格と重ならないときだけ出す
  if (fresh.length > 0 && !eraUp) {
    const cp = fresh[fresh.length - 1];
    showToast(`${cp.em} <b>${cp.label}</b><br><span class="tiny">${cp.note}</span>`, 4);
  }

  progress.eraId = eraForEvo(progress.evo).era.id;
  return { eraUp, checkpoints: fresh };
}

/**
 * 正解したときの一括処理。
 * @param {object} problem
 * @param {number} elapsedMs
 * @returns {{ gain:number, firstTime:boolean, eraUp:object|null, badges:array, mission:object }}
 */
export function awardCorrect(problem, elapsedMs) {
  const firstTime = !progress.solved[problem.id];
  const before = progress.evo;
  const whereBefore = eraForEvo(before);

  recordAttempt(problem.id, true, elapsedMs);

  const gain = solveEvo(problem.diff, firstTime);
  addEvo(gain);
  if (firstTime) recordCorrect(problem.id);

  playSound("correct");
  startConfetti(120);
  floatEvo(fmtEvo(gain));
  if (progress.combo >= 2) {
    playSound("combo");
    comboFloat(progress.combo);
  }

  // 間違えたまま残っていた問題なら「克服」として記録する（登録者限定）
  const overcame = clearMiss(problem.id);

  const { eraUp } = checkJourneyProgress(before, progress.evo);

  // デイリーミッション（解いた直後に明示的に判定する）
  const mission = claimDailyMission();
  if (mission.claimed) {
    const mBefore = progress.evo - mission.bonus;
    checkJourneyProgress(mBefore, progress.evo);
    showToast(`🎯 デイリーミッション達成！<br>ボーナス <b>+${fmtEvo(mission.bonus)}</b>`, 4);
  }

  const badges = checkNewBadges();
  for (const b of badges) {
    showToast(`${b.em} バッジ獲得：<b>${b.name}</b><br><span class="tiny">${b.desc}</span>`, 4);
  }

  save();
  const whereAfter = eraForEvo(progress.evo);
  return {
    gain,
    firstTime,
    eraUp,
    badges,
    mission,
    overcame,
    // 「進化ポイント +0.05」より「46億年前 → 45.2億年前」のほうが伝わるので、
    // 問題画面ではこの2つを見せる
    from: whereBefore,
    to: whereAfter,
  };
}

/**
 * 不正解のときの処理。
 * @returns {{ recorded: boolean }} recorded＝間違えた問題として記録できた
 */
export function awardWrong(problem, elapsedMs) {
  recordAttempt(problem.id, false, elapsedMs);
  breakCombo();
  playSound("wrong");
  const recorded = recordMiss(problem.id);
  save();
  return { recorded };
}

/** 未解答の問題で解説を先に見たとき（獲得なし・コンボ切れ） */
export function forfeitProblem() {
  breakCombo();
  save();
}
